import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const ASSET_GROUPS = {
  'Energy': ['BRENT', 'WTI'],
  'Metals': ['GOLD', 'SILVER', 'COPPER'],
  'Forex': ['DXY', 'EUR/USD', 'GBP/USD', 'USD/CNY'],
  'Indices': ['SPX', 'NDX', 'VIX'],
  'Crypto': ['BTC', 'ETH', 'XRP', 'SOL', 'HBAR'],
  'Rates': ['US10Y'],
};

function PriceCard({ symbol, price, change, unavailable }) {
  if (unavailable) {
    return (
      <div className="bg-korinth-bg rounded px-3 py-2 border border-korinth-border/30 opacity-50" title="Requires paid data tier">
        <div className="flex items-center justify-between">
          <span className="text-xs font-mono text-korinth-muted">{symbol}</span>
          <Minus className="w-3 h-3 text-korinth-muted/50" />
        </div>
        <div className="text-sm font-mono font-semibold text-korinth-muted mt-1">N/A</div>
        <div className="text-[10px] font-mono text-korinth-muted/40">paid tier</div>
      </div>
    );
  }

  const isPositive = change > 0;
  const isNeutral = change === 0 || change === null;
  const Icon = isPositive ? TrendingUp : isNeutral ? Minus : TrendingDown;
  const color = isPositive ? 'text-korinth-accent' : isNeutral ? 'text-korinth-muted' : 'text-korinth-danger';

  return (
    <div className="bg-korinth-bg rounded px-3 py-2 border border-korinth-border/50">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-korinth-muted">{symbol}</span>
        <Icon className={`w-3 h-3 ${color}`} />
      </div>
      <div className="text-sm font-mono font-semibold text-white mt-1">
        {price !== null ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
      </div>
      <div className={`text-xs font-mono ${color}`}>
        {change !== null ? `${isPositive ? '+' : ''}${change.toFixed(2)}%` : '—'}
      </div>
    </div>
  );
}

export default function CrossAssetDashboard() {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPrices() {
      try {
        const res = await fetch('/api/prices/latest');
        if (res.ok) {
          const data = await res.json();
          setPrices(data);
        }
      } catch {
        // Backend not running yet — show placeholder
      } finally {
        setLoading(false);
      }
    }
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ModuleContainer title="Cross-Asset Dashboard" subtitle="Live prices across energy, metals, forex, indices, crypto, and rates">
      {Object.entries(ASSET_GROUPS).map(([group, symbols]) => (
        <div key={group} className="mb-4 last:mb-0">
          <h3 className="text-xs font-mono text-korinth-muted uppercase tracking-wider mb-2">{group}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {symbols.map((symbol) => (
              <PriceCard
                key={symbol}
                symbol={symbol}
                price={prices[symbol]?.price ?? null}
                change={prices[symbol]?.change_pct ?? null}
                unavailable={prices[symbol]?.unavailable || false}
              />
            ))}
          </div>
        </div>
      ))}
      {loading && (
        <div className="text-center text-korinth-muted text-xs py-4 font-mono">
          Connecting to price feeds...
        </div>
      )}
    </ModuleContainer>
  );
}
